import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL, BASE_URL } from '../config';
import { useSocket } from '../contexts/SocketContext';
import {
  anunciarConsultorioLibre,
  anunciarLlamarPaciente,
  desbloquearVoz,
  hablaDisponible,
  precargarVoces,
  setVozHabilitada,
} from '../utils/tableroVoz';
import './TableroCitas.css';

interface TurnoSala {
  id: number;
  hora: string;
  paciente: string;
  medico: string;
  especialidad: string | null;
  consultorio: string | null;
}

interface TableroCitasData {
  fecha: string;
  actualizado: string;
  laboratorio: { nombre: string; logo_url: string };
  ahora: TurnoSala[];
  cola: TurnoSala[];
}

function urlLogo(logo: string | undefined): string | null {
  if (!logo) return null;
  if (logo.startsWith('http') || logo.startsWith('data:')) return logo;
  // Logos viven en la API (/uploads), no en el origen del SPA
  const base = String(BASE_URL || '').replace(/\/$/, '');
  return `${base}${logo.startsWith('/') ? '' : '/'}${logo}`;
}

function fechaLarga(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('es-NI', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

const TableroCitas: React.FC = () => {
  const { socket } = useSocket();
  const [data, setData] = useState<TableroCitasData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avisoSuave, setAvisoSuave] = useState<string | null>(null);
  const [ahoraReloj, setAhoraReloj] = useState(() => new Date());
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [anuncioVivo, setAnuncioVivo] = useState('');
  const colaRef = useRef<HTMLOListElement>(null);
  const prevAhoraRef = useRef<Map<number, TurnoSala> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const tieneDatosRef = useRef(false);

  const cargar = useCallback(async () => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const res = await axios.get<TableroCitasData>(`${API_URL}/public/tablero-citas`, {
        signal: ac.signal,
      });
      tieneDatosRef.current = true;
      setData(res.data);
      setError(null);
      setAvisoSuave(null);
    } catch (e: any) {
      if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
      const status = e?.response?.status;
      const msg =
        e?.response?.data?.error ||
        (status === 403
          ? 'El módulo de clínica no está activo. Actívelo en configuración para usar esta pantalla.'
          : status === 404
            ? 'El servidor aún no tiene la pantalla de citas. Reinicie la API e intente de nuevo.'
            : 'No se pudo actualizar la pantalla. Se reintentará en unos segundos.');
      if (tieneDatosRef.current) {
        setAvisoSuave(String(msg));
      } else {
        setError(String(msg));
      }
    }
  }, []);

  useEffect(() => {
    document.body.classList.add('tablero-pagina');
    setVozHabilitada(true);
    precargarVoces();
    if (hablaDisponible()) desbloquearVoz();
    const desbloquear = () => {
      if (hablaDisponible()) desbloquearVoz();
    };
    window.addEventListener('pointerdown', desbloquear, { once: true, passive: true });
    window.addEventListener('keydown', desbloquear, { once: true });
    const onFs = () => setPantallaCompleta(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      document.body.classList.remove('tablero-pagina');
      window.removeEventListener('pointerdown', desbloquear);
      window.removeEventListener('keydown', desbloquear);
      document.removeEventListener('fullscreenchange', onFs);
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const nombre = data?.laboratorio.nombre?.trim();
    document.title = nombre ? `${nombre} · Sala de espera` : 'Sala de espera';
  }, [data?.laboratorio.nombre]);

  /** Llamar al pasar a “en consulta”; anunciar libre al salir. */
  useEffect(() => {
    if (!data) return;
    const actual = new Map(data.ahora.map((t) => [t.id, t]));
    const prev = prevAhoraRef.current;
    if (prev) {
      actual.forEach((turno, id) => {
        if (!prev.has(id)) {
          anunciarLlamarPaciente(turno.paciente, turno.consultorio, id);
          const cons = turno.consultorio ? `, consultorio ${turno.consultorio}` : '';
          setAnuncioVivo(`${turno.paciente} pase a consulta${cons}.`);
        }
      });
      prev.forEach((turno, id) => {
        if (!actual.has(id)) {
          anunciarConsultorioLibre(turno.consultorio, id);
          const cons = turno.consultorio || 'Un consultorio';
          setAnuncioVivo(
            turno.consultorio
              ? `Consultorio ${cons} disponible.`
              : 'Un consultorio ha quedado disponible.'
          );
        }
      });
    }
    prevAhoraRef.current = actual;
  }, [data]);

  useEffect(() => {
    void cargar();
    const poll = window.setInterval(() => void cargar(), 8000);
    return () => window.clearInterval(poll);
  }, [cargar]);

  useEffect(() => {
    if (!socket) return;
    const onCambio = () => void cargar();
    socket.on('tablero-citas', onCambio);
    return () => {
      socket.off('tablero-citas', onCambio);
    };
  }, [socket, cargar]);

  useEffect(() => {
    const id = window.setInterval(() => setAhoraReloj(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const el = colaRef.current;
    if (mq.matches || !el) return;
    let dir = 1;
    let frame = 0;
    const tick = () => {
      const max = el.scrollHeight - el.clientHeight;
      if (max > 12) {
        el.scrollTop += dir * 0.35;
        if (el.scrollTop >= max - 1) dir = -1;
        if (el.scrollTop <= 1) dir = 1;
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [data?.cola.length]);

  const reloj = ahoraReloj.toLocaleTimeString('es-NI', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const logo = urlLogo(data?.laboratorio.logo_url);
  const nombreLab = data?.laboratorio.nombre || 'Laboratorio clínico';
  const enConsulta = data?.ahora.length ?? 0;
  const enEspera = data?.cola.length ?? 0;

  return (
    <main className="tablero">
      <header className="tablero-top">
        <div className="tablero-marca">
          {logo ? (
            <div className="tablero-logo-wrap">
              <img className="tablero-logo" src={logo} alt="" />
            </div>
          ) : null}
          <div>
            <h1 className="tablero-nombre">{nombreLab}</h1>
            <p className="tablero-fecha">{data ? fechaLarga(data.fecha) : 'Citas de hoy'}</p>
          </div>
        </div>
        <div className="tablero-reloj">
          <p className="tablero-hora" aria-hidden="true">
            {reloj}
          </p>
          <span className="visually-hidden">Hora actual: {reloj}</span>
        </div>
      </header>

      <p className="visually-hidden" aria-live="polite">
        {anuncioVivo ||
          (data
            ? `${enConsulta} en consulta, ${enEspera} en espera.`
            : error
              ? error
              : 'Cargando citas del día.')}
      </p>

      {avisoSuave ? (
        <p className="tablero-aviso" role="status">
          {avisoSuave}
        </p>
      ) : null}

      {!data && !error ? (
        <div className="tablero-estado" aria-busy="true" aria-label="Cargando citas de hoy">
          <div className="tablero-estado-inner">
            <div className="tablero-spinner" aria-hidden="true" />
            <p>Obteniendo la agenda del día…</p>
          </div>
        </div>
      ) : null}

      {error && !data ? (
        <div className="tablero-estado" role="alert">
          <div className="tablero-estado-inner">
            <p className="tablero-error">{error}</p>
          </div>
        </div>
      ) : null}

      {data ? (
        <div className="tablero-grid">
          <section className="tablero-panel" aria-labelledby="titulo-ahora">
            <h2 id="titulo-ahora">En consulta</h2>
            {data.ahora.length === 0 ? (
              <p className="sala-vacio">Ningún consultorio está atendiendo en este momento.</p>
            ) : (
              <ul className="tablero-ahora">
                {data.ahora.map((turno) => (
                  <li className="sala" key={turno.id}>
                    <div
                      className={
                        turno.consultorio ? 'sala-num-wrap' : 'sala-num-wrap sin-numero'
                      }
                      aria-label={
                        turno.consultorio
                          ? `Consultorio ${turno.consultorio}`
                          : 'Consultorio sin número'
                      }
                    >
                      <div className={turno.consultorio ? 'sala-num' : 'sala-num sin-numero'}>
                        {turno.consultorio || 'Sin número'}
                      </div>
                    </div>
                    <div className="sala-texto">
                      <p className="sala-paciente">{turno.paciente}</p>
                      <p className="sala-medico">{turno.medico}</p>
                      {turno.especialidad ? <p className="sala-esp">{turno.especialidad}</p> : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="tablero-panel tablero-cola" aria-labelledby="titulo-cola">
            <h2 id="titulo-cola">Siguiente</h2>
            {data.cola.length === 0 ? (
              <p className="cola-vacio">No hay más citas pendientes hoy.</p>
            ) : (
              <ol className="cola" ref={colaRef}>
                {data.cola.map((turno) => (
                  <li className="turno" key={turno.id}>
                    <div className="turno-hora">{turno.hora}</div>
                    <div>
                      <p className="turno-paciente">{turno.paciente}</p>
                      <p className="turno-meta">
                        {turno.medico}
                        {turno.consultorio ? ` · Consultorio ${turno.consultorio}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      ) : null}

      <div className="tablero-acciones">
        <button
          type="button"
          className="tablero-fs"
          aria-pressed={pantallaCompleta}
          onClick={() => {
            if (hablaDisponible()) desbloquearVoz();
            const el = document.documentElement;
            if (!document.fullscreenElement) {
              void el.requestFullscreen?.();
            } else {
              void document.exitFullscreen?.();
            }
          }}
        >
          {pantallaCompleta ? 'Salir de pantalla completa' : 'Pantalla completa'}
        </button>
      </div>
    </main>
  );
};

export default TableroCitas;
